// v50.212 — NAV_AREAS.
//
// Phase 1 of the navigation IA redesign (see Build notes/labourcore-nav-redesign-brief.md).
// Single source of truth for the role-aware primary rail + the grouped
// secondary vertical sub-nav. Replaces the flat 14-item rail in nav.ts.
//
// Pragmatic deviation from the brief: many of the new sub-routes the
// brief specifies (e.g. `/find-labour/suppliers`, `/inbox/unread`) don't
// exist as React-router endpoints yet — their host pages serve their
// filters via query-params today, not URL paths. To ship Phase 1
// against the "no new screens required" constraint, each NAV item
// records the brief's canonical path in `briefRoute` but navigates the
// user to `route` (the existing legacy endpoint). When subsequent
// phases migrate the sub-tabs to real route segments, swap `route` =
// `briefRoute` and the rail keeps tracking.
//
// Top-level new paths (`/today`, `/find-labour`, `/find-work`, `/my-crew`)
// DO get real Navigate redirects in App.tsx so deep links survive.

import type { AdminTier, Role } from "@/lib/api/types";
import type { IconName } from "@/components/Icon";

/* v50.321 — per-tier admin link sets. master = full back office; board =
   view-only oversight (no emails / billing); investor = revenue overview.
   v50.324 — moved here from Sidebar.tsx so the mobile nav sheet shares it. */
export const ADMIN_NAV: Record<AdminTier, { label: string; links: Array<{ to: string; label: string }> }> = {
  master: {
    label: "Admin",
    links: [
      { to: "/admin",              label: "Dashboard" },
      { to: "/admin/onboarding",   label: "Onboarding queue" },
      { to: "/admin/originations", label: "Origination queue" },
      { to: "/admin/companies",    label: "Companies" },
      /* The control plane. Built with the Spine but never linked, so it was
         reachable only by typing the URL. Sits under Companies because that is
         where an admin is when they realise they need it — "this company's
         owner has left" starts on the company list. master only: `board` is
         read-only and this screen exists to change things. */
      { to: "/admin/access",       label: "Access & roles" },
      { to: "/admin/cities",       label: "Cities" },
      { to: "/admin/emails",       label: "Emails" },
      { to: "/admin/billing",      label: "Billing" },
      { to: "/admin/market-report", label: "Market report" },
      { to: "/admin/reference-review", label: "Reference data review" },
      { to: "/admin/investor",     label: "Investor overview" },
    ],
  },
  board: {
    label: "Board oversight",
    links: [
      { to: "/admin",              label: "Dashboard" },
      { to: "/admin/onboarding",   label: "Onboarding queue" },
      { to: "/admin/originations", label: "Origination queue" },
      { to: "/admin/companies",    label: "Companies" },
      { to: "/admin/cities",       label: "Cities" },
      { to: "/admin/reference-review", label: "Reference data review" },
    ],
  },
  investor: {
    label: "Investor",
    links: [
      { to: "/admin/investor",     label: "Overview" },
    ],
  },
};

export type SecondaryStatus = "now" | "new";

export interface SecondaryItem {
  id:          string;
  label:       string;
  /* Where the rail actually navigates the user today. May equal
     briefRoute once the underlying page splits its tabs into routes. */
  route:       string;
  /* The canonical path per the brief §3 — preserved so future migrations
     can swap to it without changing the labels. */
  briefRoute?: string;
  status:      SecondaryStatus;
  /* Optional section header. Items with the same group render under one
     quiet sentence-case header in the secondary panel. */
  group?:      string;
}

export type AreaRoles = "universal" | Role[];

/** The five spine positions. */
export type Tier = "owner" | "consultant" | "tier1" | "mep" | "operative";

/**
 * WHICH TIERS SEE THIS AREA.
 *
 * NAVIGATION IS TIER-DRIVEN. The active company decides the acting tenant and
 * the entitled tier set; the active TIER decides the rail. Membership
 * authority controls which ACTIONS are offered — never the rail's structure, so
 * a viewer and an owner at the same Tier-1 company see the same taxonomy and
 * the viewer simply cannot action some of it.
 *
 * There is deliberately NO per-company navigation configuration, and none
 * should be added. The rail for a tier is the same for every company on it —
 * that is what makes "one platform, five cockpits" true rather than a slogan.
 * Once one company has an override, the rail stops being derivable from the
 * tier and every future change has to be reconciled against a list of
 * exceptions nobody maintains.
 */
export type AreaTiers = "universal" | Tier[];

/**
 * Roles map onto tiers during the migration window, so an area that has not
 * declared `tiers` yet still gates correctly.
 *
 *   coordinator → tier1        partner → mep
 *   hybrid      → both, because a hybrid is a company holding two grants
 */
const ROLE_TIERS: Record<string, Tier[]> = {
  coordinator: ["tier1"],
  partner: ["mep"],
  hybrid: ["tier1", "mep"],
};

export interface NavArea {
  id:          string;
  label:       string;
  icon:        IconName;
  /* Canonical brief path. Real navigation may resolve to a legacy path
     via Navigate redirect (App.tsx) until the page splits. */
  route:       string;
  /* Legacy path the area's primary destination currently lives at. Used
     for active-state matching so the rail highlights correctly when the
     user is on the old URL. */
  legacyRoute?: string;
  roles:       AreaRoles;
  /** Preferred over `roles` when present. See AreaTiers. */
  tiers?:      AreaTiers;
  /* null means no secondary sub-nav. Today and Insights are composed
     single surfaces per §1. */
  secondary:   SecondaryItem[] | null;
}

export const NAV_AREAS: NavArea[] = [
  {
    id: "today", label: "Today", icon: "circle", route: "/today", legacyRoute: "/dashboard",
    roles: "universal", secondary: null,
  },
  {
    id: "inbox", label: "Inbox", icon: "mail", route: "/inbox",
    roles: "universal",
    secondary: [
      /* v50.169 fix (#150) — routes below use the real ?filter= contract
         (see App.tsx redirects + InboxPage.tsx) instead of the stale
         /inbox/<sub> path form, so Sidebar's isSecondaryActive (which
         only compares querystrings when the item's own route has one)
         can actually tell these apart instead of them all collapsing
         onto the pathname-only "inbox" match. */
      { id: "inbox",       label: "Inbox",       route: "/inbox",                     briefRoute: "/inbox",             status: "now", group: "Messages" },
      { id: "unread",      label: "Unread",      route: "/inbox?filter=unread",       briefRoute: "/inbox/unread",      status: "now", group: "Messages" },
      { id: "invitations", label: "Invitations", route: "/inbox?filter=invitations",  briefRoute: "/inbox/invitations", status: "now", group: "Messages" },
      { id: "responses",   label: "Responses",   route: "/inbox?filter=responses",    briefRoute: "/inbox/responses",   status: "now", group: "Messages" },
      { id: "sent",        label: "Sent",        route: "/inbox?filter=sent",         briefRoute: "/inbox/sent",        status: "now", group: "Messages" },
      { id: "by-partner",  label: "By partner",  route: "/inbox?filter=by_partner",   briefRoute: "/inbox/by-partner",  status: "now", group: "Filters"  },
      { id: "by-stage",    label: "By stage",    route: "/inbox?filter=by_stage",     briefRoute: "/inbox/by-stage",    status: "now", group: "Filters"  },
    ],
  },
  {
    id: "find-labour", label: "Find labour", icon: "search", route: "/find-labour", legacyRoute: "/partners",
    roles: ["coordinator", "hybrid"],
    secondary: [
      { id: "suppliers",      label: "Find suppliers", route: "/partners",                  briefRoute: "/find-labour/suppliers",  status: "now", group: "Discover" },
      { id: "map",            label: "Map",            route: "/find-labour/map",           briefRoute: "/find-labour/map",        status: "now", group: "Discover" },
      /* v50.396 — Move 3: relabel "Shortlists" to its actual content
         ("Tried & Tested"). The route stays the same so muscle memory
         on the URL still works. */
      { id: "shortlists",     label: "Tried & Tested", route: "/partners?tab=tried-tested", briefRoute: "/find-labour/shortlists", status: "now", group: "Saved"    },
      { id: "saved-searches", label: "Saved searches", route: "/settings?section=searches", briefRoute: "/find-labour/searches",   status: "now", group: "Saved"    },
      { id: "network",        label: "Network",        route: "/partners?tab=network",      briefRoute: "/find-labour/network",    status: "now", group: "Saved"    },
    ],
  },
  {
    id: "find-work", label: "Find work", icon: "briefcase", route: "/find-work", legacyRoute: "/requirements",
    roles: ["partner", "hybrid"],
    secondary: [
      /* GH #230 — "Live requirements" first, ahead of "Open tenders".
         Clicking the parent "Find work" item lands on /requirements (see
         legacyRoute above), so the landing page was listed second and the
         first item in the list went somewhere else. Order now matches where
         the parent actually takes you. */
      /* v50.169 fix (#96) — was "Requirements feed"; renamed to match the
         page it actually opens (RequirementsPage.tsx's h1 says "Live
         Requirements") and the Projects area's own entry for the same
         route, so the two nav paths converging on this page no longer
         show two different names for it. */
      { id: "requirements", label: "Live requirements",  route: "/requirements",           briefRoute: "/find-work/requirements", status: "now", group: "Opportunities" },
      { id: "tenders",      label: "Open tenders",       route: "/tenders",                briefRoute: "/find-work/tenders",      status: "now", group: "Opportunities" },
      { id: "matched",      label: "Matched to my crew", route: "/requirements?tab=matched", briefRoute: "/find-work/matched",   status: "now", group: "Opportunities" },
      { id: "bids-active",  label: "Active bids",        route: "/tenders/me",             briefRoute: "/find-work/bids",         status: "now", group: "My bids" },
      { id: "bids-short",   label: "Shortlisted",        route: "/tenders/me?tab=shortlisted", briefRoute: "/find-work/bids/short",status: "now", group: "My bids" },
      { id: "bids-result",  label: "Won / Lost",         route: "/tenders/me?tab=result",  briefRoute: "/find-work/bids/result",  status: "now", group: "My bids" },
    ],
  },
  {
    id: "projects", label: "Projects", icon: "folder", route: "/projects",
    roles: ["coordinator", "hybrid"],
    secondary: [
      { id: "my-projects",   label: "My projects",         route: "/projects",                  briefRoute: "/projects",              status: "now", group: "Demand"   },
      { id: "requirements",  label: "Live requirements",   route: "/requirements",              briefRoute: "/requirements",          status: "now", group: "Demand"   },
      { id: "tenders-coord", label: "Manage tenders",      route: "/tenders",                   briefRoute: "/tenders",               status: "now", group: "Demand"   },
      /* v50.387 — Designer Role: global view of every design engagement
         on the coord's projects. Empty until a project is flagged. */
      { id: "designs",       label: "Designs",             route: "/designs",                   briefRoute: "/designs",               status: "now", group: "Demand"   },
      { id: "deals",         label: "Active deals",        route: "/deals",                     briefRoute: "/deals",                 status: "now", group: "Delivery" },
      { id: "site-ops",      label: "Site ops & diary",    route: "/projects/site-ops",         briefRoute: "/projects/site-ops",     status: "now", group: "Delivery" },
      { id: "calendar",      label: "Calendar",            route: "/projects/calendar",         briefRoute: "/projects/calendar",     status: "now", group: "Delivery" },
      { id: "futures",       label: "Labour futures",      route: "/futures",                   briefRoute: "/futures",               status: "now", group: "Pipeline" },
    ],
  },
  {
    id: "my-crew", label: "My crew", icon: "users", route: "/my-crew", legacyRoute: "/workers",
    roles: ["partner", "hybrid"],
    secondary: [
      { id: "operatives",   label: "Operatives",      route: "/workers",              briefRoute: "/workers",            status: "now", group: "Roster" },
      { id: "availability", label: "Availability",    route: "/my-crew/availability", briefRoute: "/my-crew/availability",status: "now", group: "Roster" },
      { id: "crew-comp",    label: "Crew compliance", route: "/compliance",           briefRoute: "/compliance",         status: "now", group: "Roster" },
      { id: "deals-p",      label: "Active deals",    route: "/deals",                briefRoute: "/deals",              status: "now", group: "Work"   },
      /* v50.269 — partner-side Site Ops mirror. Same route as the coord
         entry at /projects/site-ops; the page role-branches its tab
         content so partners see their own flagged/draft exceptions,
         hidden bulk-confirm, and a coord-grouped rollup. */
      { id: "site-ops-p",   label: "Site ops & diary",route: "/projects/site-ops",    briefRoute: "/projects/site-ops",  status: "now", group: "Work"   },
      { id: "futures-p",    label: "Labour futures",  route: "/futures",              briefRoute: "/futures",            status: "now", group: "Work"   },
    ],
  },
  {
    id: "compliance", label: "Compliance", icon: "shield", route: "/compliance",
    roles: "universal",
    secondary: [
      { id: "upload",    label: "Upload",             route: "/compliance/upload",   briefRoute: "/compliance/upload",   status: "now", group: "Wallet" },
      { id: "verify",    label: "Compliance inbox",   route: "/compliance/inbox",    briefRoute: "/compliance/inbox",    status: "now", group: "Wallet" },
      { id: "expiry",    label: "Expiry tracker",     route: "/compliance/expiry",   briefRoute: "/compliance/expiry",   status: "now", group: "Wallet" },
      { id: "certs",     label: "Verified certs",     route: "/compliance/certs",    briefRoute: "/compliance/certs",    status: "now", group: "Wallet" },
      { id: "audit",     label: "Audit log",          route: "/audit",                   briefRoute: "/audit",               status: "now", group: "Trail"  },
      { id: "documents", label: "Documents",          route: "/compliance/documents",    briefRoute: "/compliance/documents",status: "now", group: "Trail"  },
    ],
  },
  {
    id: "payments", label: "Payments", icon: "coins", route: "/payments",
    roles: "universal",
    secondary: [
      { id: "invoices",   label: "Invoices",          route: "/payments/invoices", briefRoute: "/payments/invoices", status: "now" },
      { id: "payments",   label: "Payments",          route: "/payments",          briefRoute: "/payments",          status: "now" },
      { id: "intro-fees", label: "Introduction fees", route: "/payments/fees",     briefRoute: "/payments/fees",     status: "now" },
      /* v50.328 — Defence P2: e-signed framework agreements. */
      { id: "frameworks", label: "My frameworks",     route: "/frameworks",        briefRoute: "/frameworks",        status: "now" },
      { id: "disputes",   label: "Disputes",          route: "/disputes",          briefRoute: "/disputes",          status: "now" },
    ],
  },
  {
    id: "insights", label: "Insights", icon: "trend-up", route: "/insights",
    roles: "universal", secondary: null,
  },
  /* v50.341 — Defence P16: coordinator community. Coord + hybrid only;
     server-side RLS via lc_is_verified_coordinator() is the real gate, the
     rail entry is hidden for partners as a courtesy so it doesn't look
     broken when they click and get a "verified coordinators only" panel. */
  {
    id: "community", label: "Community", icon: "users", route: "/community",
    roles: ["coordinator", "hybrid"], secondary: null,
  },
  {
    id: "settings", label: "Settings", icon: "tools", route: "/settings",
    roles: "universal",
    secondary: [
      { id: "account",      label: "Account",                 route: "/settings/account",       briefRoute: "/settings/account",       status: "now", group: "Account"      },
      { id: "security",     label: "Security",                route: "/settings/security",      briefRoute: "/settings/security",      status: "now", group: "Account"      },
      { id: "notif-prefs",  label: "Notification preferences",route: "/settings/notifications", briefRoute: "/settings/notifications", status: "now", group: "Account"      },
      { id: "company",      label: "Company",                 route: "/settings/company",       briefRoute: "/settings/company",       status: "now", group: "Organisation" },
      { id: "team",         label: "Team",                    route: "/settings/team",          briefRoute: "/settings/team",          status: "now", group: "Organisation" },
      { id: "subscription", label: "Subscription & plan",     route: "/settings/billing",       briefRoute: "/settings/billing",       status: "now", group: "Organisation" },
      { id: "ai-matching",  label: "AI matching",             route: "/settings/ai-matching",   briefRoute: "/settings/ai-matching",   status: "now", group: "Platform"     },
      { id: "saved",        label: "Saved searches",          route: "/settings/saved-searches",briefRoute: "/settings/saved-searches",status: "now", group: "Platform"     },
      { id: "your-data",    label: "Your data",               route: "/settings/data",          briefRoute: "/settings/data",          status: "now", group: "Data & legal" },
      { id: "legal",        label: "Legal",                   route: "/settings/legal",         briefRoute: "/settings/legal",         status: "now", group: "Data & legal" },
      { id: "danger",       label: "Danger zone",             route: "/settings/danger",        briefRoute: "/settings/danger",        status: "now", group: "Data & legal" },
    ],
  },
];

/* TODO(both-merge): future simplification per brief §2 — merge
   Find labour + Find work into a single Marketplace area with a
   buy/sell toggle, and Projects + My crew into a single Work area. */

export function railFor(role: Role | undefined | null): NavArea[] {
  if (!role) return [];
  return NAV_AREAS.filter(a =>
    a.roles === "universal" || a.roles.includes(role)
  );
}

/**
 * The rail for a tier. This is the one the shell should call.
 *
 * An area declaring `tiers` is gated on it directly. An area that has not been
 * migrated yet falls back to its `roles`, translated through ROLE_TIERS — so
 * the two axes can coexist while areas move across, and nothing has to be
 * converted in one commit.
 *
 * Owner, consultant and operative currently match only `universal` areas plus
 * anything that has explicitly named them. That is correct rather than
 * incomplete: their rails are net-new and land as those areas are authored.
 */
export function railForTier(tier: Tier | undefined | null): NavArea[] {
  if (!tier) return [];
  return NAV_AREAS.filter(a => {
    if (a.tiers) return a.tiers === "universal" || a.tiers.includes(tier);
    if (a.roles === "universal") return true;
    /* Not yet migrated: does any role that maps to this tier grant it? */
    return a.roles.some(r => (ROLE_TIERS[r] ?? []).includes(tier));
  });
}

/* Match active area by current pathname. Considers the area's
   canonical route, its legacy route, and any secondary item's route
   prefix so the rail highlights correctly regardless of which path
   variant the user lands on. */
export function activeAreaFor(pathname: string, search: string = "", candidateAreas: NavArea[] = NAV_AREAS): NavArea | null {
  /* Longest-prefix wins so /projects/foo doesn't fall through to /.
     A secondary item's own area.route match (len === Infinity) always
     outranks a same-length secondary-item match from a different area,
     so e.g. plain /settings highlights Settings even though "Saved
     searches" under Find labour also resolves to a /settings path.
     v50.169 fix (#153) — callers should pass the role-filtered rail
     (railFor(role)) as candidateAreas. Without it, an area the user
     can't even see (e.g. "Find work", coordinator-hidden) could still
     win the match via its legacyRoute — that's exactly why /requirements
     resolved to "Find work" instead of "Projects" for a coordinator
     posting a requirement, leaving Projects' secondary nav unlit. */
  const matches: { area: NavArea; len: number }[] = [];
  const haveParams = new URLSearchParams(search);
  for (const area of candidateAreas) {
    const candidates = [area.route, area.legacyRoute].filter(Boolean) as string[];
    for (const r of candidates) {
      if (pathname === r || pathname.startsWith(r + "/")) {
        matches.push({ area, len: Infinity });
      }
    }
    if (area.secondary) {
      for (const s of area.secondary) {
        const [path, qs] = s.route.split("?");
        if (pathname !== path && !pathname.startsWith(path + "/")) continue;
        if (qs) {
          const wantParams = new URLSearchParams(qs);
          const allMatch = [...wantParams.entries()].every(([k, v]) => haveParams.get(k) === v);
          if (!allMatch) continue;
        }
        matches.push({ area, len: path.length });
      }
    }
  }
  if (matches.length === 0) return null;
  matches.sort((a, b) => b.len - a.len);
  return matches[0].area;
}

/* Group an area's secondary items by their `group` field. Items without
   a group land in a leading bucket whose key is the empty string so the
   rail renders them above the labelled sections (preserves order). */
export function groupSecondary(items: SecondaryItem[]): Array<{ group: string; items: SecondaryItem[] }> {
  const map = new Map<string, SecondaryItem[]>();
  const order: string[] = [];
  for (const it of items) {
    const key = it.group || "";
    if (!map.has(key)) { map.set(key, []); order.push(key); }
    map.get(key)!.push(it);
  }
  return order.map(k => ({ group: k, items: map.get(k)! }));
}
